import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Brain, MessageSquareText, Zap, Target, Lightbulb, Code } from 'lucide-react';
import { getContentConfig } from '../../../config/configLoader';
import contentService from '../../../services/contentService';
import { useTheme } from '../../../contexts/ThemeContext';

const containerVariants = {
  offscreen: { opacity: 0 },
  onscreen: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 }
  }
};

const cardVariants = {
  offscreen: { opacity: 0, y: 30, scale: 0.95 },
  onscreen: { 
    opacity: 1, y: 0, scale: 1,
    transition: { type: 'spring', stiffness: 260, damping: 20 }
  }
};

export const IntroSection = () => {
  const contentConfig = getContentConfig();
  const { isDark } = useTheme();
  const fallback = contentConfig?.intro || { cards: [], paragraphs: [] };

  const [content, setContent] = useState(fallback);

  useEffect(() => {
    contentService.getByType('intro')
      .then(({ data }) => {
        if (data && (data.cards || data.paragraphs)) setContent(prev => ({ ...prev, ...data }))
      })
      .catch(() => {/* keep config.json fallback */})
  }, []);
  
  const iconMap = { Brain, BookOpen, MessageSquareText, Zap, Target, Lightbulb, Code };

  return (
    <div className="w-full max-w-4xl mx-auto mb-12 px-4">
      <div className="space-y-8">
        {/* Feature cards */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-3 gap-4"
          variants={containerVariants}
          initial="offscreen"
          whileInView="onscreen"
          viewport={{ once: true, margin: '-50px' }}
        >
          {content.cards.map((card, index) => {
            const Icon = iconMap[card.icon] || Brain;
            return (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{ 
                  y: -4, 
                  scale: 1.02,
                  transition: { duration: 0.2 } 
                }}
                className={`
                  p-5 rounded-xl transition-all duration-300 cursor-default border-gradient
                  ${isDark 
                    ? 'glass-futuristic hover:shadow-lg hover:shadow-accent-500/10' 
                    : 'glass-futuristic-light hover:shadow-lg hover:shadow-accent-500/10'
                  }
                `}
              >
                <div className="flex items-center gap-3 mb-3">
                  <motion.div 
                    className={`p-2 rounded-lg ${isDark ? 'bg-accent-500/10 text-accent-400' : 'bg-accent-50 text-accent-600'}`}
                    whileHover={{ rotate: [0, -10, 10, 0], transition: { duration: 0.4 } }}
                  >
                    <Icon className="w-5 h-5" />
                  </motion.div>
                  <h3 className={`font-semibold text-sm ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
                    {card.title}
                  </h3>
                </div>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
                  {card.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Content paragraphs */}
        {content.paragraphs.length > 0 && (
          <motion.div 
            className="max-w-2xl mx-auto space-y-4 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-30px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {content.paragraphs.map((paragraph, index) => (
              <p
                key={index}
                className={`text-sm leading-relaxed ${
                  isDark ? 'text-neutral-400' : 'text-neutral-600'
                }`}
              >
                {paragraph}
              </p>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};